/** @jsxImportSource theme-ui */
import { Flex, Text, Spinner, Box, Divider, Heading } from 'theme-ui';
import { useEffect } from 'react';
import { Table } from '@component-controls/components';
import { JSONTree } from 'react-json-tree';
import { useSearchParams } from 'react-router-dom';
import qs from 'qs';
import { format } from 'date-fns';
import { useLazyGetVossInvoiceBalanceQuery } from '@/store/apiService';
import jsonTreeTheme from '@/theme/jsonTreeTheme';
import { GetVossInvoiceBalanceQuery } from '@/types/types';

type Props = {};

const invoicesTableColumns = [
    {
        columnKey: 'id',
        Header: 'Id',
        accessor: 'id',
    },
    {
        columnKey: 'invoiceStatus',
        Header: 'Invoice status',
        accessor: 'invoiceStatus',
    },
    {
        columnKey: 'paymentStatus',
        Header: 'Payment status',
        accessor: 'paymentStatus',
    },
    {
        columnKey: 'invoiceId',
        Header: 'Invoice id',
        accessor: 'invoiceId',
    },
    {
        columnKey: 'invoiceDate',
        Header: 'Invoice date',
        accessor: 'invoiceDate',
        Cell: ({ cell }) => format(new Date(cell.value), 'd MMM yyyy, p'),
    },
    {
        columnKey: 'invoicedAmount',
        Header: 'Invoiced amount',
        accessor: 'invoicedAmount',
    },
    {
        columnKey: 'unpaidAmount',
        Header: 'Unpaid amount',
        accessor: 'unpaidAmount',
    },
    {
        columnKey: 'totalAmount',
        Header: 'Total amount',
        accessor: 'totalAmount',
    },
];

const balancesTableColumns = [
    {
        columnKey: 'SubscriptionId',
        Header: 'Ids',
        accessor: (row: any) => row.subscriptions?.map((sub) => sub.id).join(','),
        Cell: ({ row, cell }) => {
            return row.original.subscriptions?.map((sub) => <Box>{sub.id}</Box>);
        },
    },
    {
        columnKey: 'subscriptions',
        Header: 'Subscriptions',
        accessor: (row: any) =>
            row.subscriptions
                ?.map(
                    (sub) =>
                        `${sub.customer.id} ${sub.customer.name} ${sub.customer.organizationNumber}`
                )
                ?.join(','),
        Cell: ({ row, cell }) => {
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={row.original.subscriptions}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            if (level === 1) {
                                return true;
                            }

                            if (keyPath.includes('customer')) {
                                return true;
                            }

                            return false;
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'totalAmount',
        Header: 'Total Amount',
        accessor: 'totalAmount',
        width: '50px',
    },
];

const VossBalances = (props: Props) => {
    const [searchParams] = useSearchParams();
    const [getVossBalance, balanceState] = useLazyGetVossInvoiceBalanceQuery();

    const customersBalances = balanceState.data?.CustomersBalances
        ? balanceState.data.CustomersBalances
        : [];

    const customersInvoices = balanceState.data?.CustomersInvoices
        ? balanceState.data.CustomersInvoices
        : [];

    useEffect(() => {
        const params = qs.parse(searchParams.toString());
        getVossBalance(params);

        if (Object.keys(params).length === 0) {
            balanceState.reset();
        }
    }, [searchParams.toString()]);

    if (balanceState.isLoading) {
        return <Spinner size={16} />;
    }

    return (
        <Flex sx={{ flexDirection: 'column', gap: 2 }}>
            {balanceState.isFetching ? (
                <Flex sx={{ justifyContent: 'center' }}>
                    <Spinner size={16} />
                </Flex>
            ) : null}

            <Heading>Customer balances</Heading>

            <Flex>
                {customersBalances.length > 0 ? (
                    <Table
                        header={true}
                        sorting={true}
                        filtering={true}
                        columns={balancesTableColumns}
                        data={customersBalances}
                    />
                ) : (
                    <Text>No customer balances found</Text>
                )}
            </Flex>

            <Divider />

            <Heading>Customer invoices</Heading>

            <Flex>
                {customersInvoices.length > 0 ? (
                    <Table
                        header={true}
                        sorting={true}
                        filtering={true}
                        columns={invoicesTableColumns}
                        data={customersInvoices}
                    />
                ) : (
                    <Text>No customer invoices found</Text>
                )}
            </Flex>
        </Flex>
    );
};

export default VossBalances;
